<template lang="html">
	<div>
		<vs-popup
			:active.sync="$props.popupActive"
			title="Gestion Netlinking"
			fullscreen
		>
			<div ref="popupContainer" class="popup-container">
				<!-- Filters -->
				<div class="filter-section">
					<FilterCard
						:isGestion="true"
						:isFilterButton="false"
						:refreshKey="1"
						:showListLength="filteredContracts.length"
						:key="filterRefreshKey"
						:isOpen="true"
						:filters="filtersTemplates"
						@filter="handleFilterEmits($event)"
						:onChangeEmit="true"
						@clearAll="resetAllFilter()"
						:showPeriodArrows="true"
					>
						<!-- Export over a period range -->
						<template slot="filter-actions">
							<vs-button
								color="primary"
								type="filled"
								icon-pack="feather"
								icon="icon-download-cloud"
								@click="showExportPopup = true"
							>
								Exporter
							</vs-button>
						</template>
					</FilterCard>
				</div>

				<!-- Summary -->
				<div class="budget-summary" v-if="!isLoading">
					<div class="summary-cards">
						<vs-card class="summary-card">
							<div class="summary-content">
								<div class="flex">
									<h4>Total Budgets</h4>
									<p class="ml-5">{{ selectedPeriod }}</p>
								</div>
								<span class="summary-amount">{{
									formatCurrency(totalBudgets)
								}}</span>
							</div>
						</vs-card>
						<vs-card class="summary-card">
							<div class="summary-content">
								<div class="flex">
									<h4>Total Consommé</h4>
									<p class="ml-5">{{ selectedPeriod }}</p>
								</div>
								<span class="summary-amount spent">{{
									formatCurrency(totalSpent)
								}}</span>
							</div>
						</vs-card>
						<vs-card class="summary-card">
							<div class="summary-content">
								<div class="flex">
									<h4>Total Disponible</h4>
									<p class="ml-5">{{ selectedPeriod }}</p>
								</div>
								<span
									:class="[
										'summary-amount',
										totalAvailable >= 0 ? 'positive' : 'negative',
									]"
									>{{ formatCurrency(totalAvailable) }}</span
								>
							</div>
						</vs-card>
						<vs-card class="summary-card">
							<div class="summary-content">
								<div class="flex">
									<h4>Backlinks</h4>
									<p class="ml-5">{{ selectedPeriod }}</p>
								</div>
								<span class="summary-amount">{{ totalNetlinks }}</span>
							</div>
						</vs-card>
					</div>
				</div>

				<!-- Data quality notes: never silently absorb what we cannot place -->
				<div
					class="coverage-warning"
					v-if="!isLoading && coverage.unassigned_count > 0"
				>
					<vs-icon icon="warning" size="small" color="warning"></vs-icon>
					<span>
						{{ coverage.unassigned_count }} backlink(s) de cette période
						({{ formatCurrency(coverage.unassigned_amount) }}) ne sont
						rattachés à aucune prestation et ne sont comptés nulle part.
					</span>
				</div>
				<div
					class="coverage-warning"
					v-if="!isLoading && accruingAfterEnd.length > 0"
				>
					<vs-icon icon="warning" size="small" color="warning"></vs-icon>
					<span>
						{{ accruingAfterEnd.length }} prestation(s) Terminé/Annulé sans
						date de fin continuent d'accumuler du budget
						({{ accruingAfterEndLabel }}). Renseignez leur date de fin pour
						arrêter le cumul.
					</span>
				</div>

				<!-- Table -->
				<div class="table-container">
					<div v-if="!isLoading" class="custom-table-wrapper">
						<table class="custom-budget-table">
							<thead>
								<tr>
									<th style="width: 20px">
										<div
											@click.stop="toggleAllExpand()"
											class="expand-icon-wrapper"
											style="cursor: pointer !important"
										>
											<vs-icon
												:icon="
													allExpanded
														? 'keyboard_arrow_down'
														: 'keyboard_arrow_right'
												"
												size="small"
												style="pointer-events: none"
											/>
										</div>
									</th>
									<th>Client</th>
									<th>Consultant</th>
									<th>N°</th>
									<th>Statut</th>
									<th>Période</th>
									<th class="text-center">Budget Période / Oneshot</th>
									<th class="text-center">Restant antérieur</th>
									<th class="text-center">Correction antérieur</th>
									<th class="text-center">Total Budget</th>
									<th class="text-center">Consommé Période</th>
									<th class="text-center">Disponible</th>
									<th class="text-center">Actions</th>
								</tr>
							</thead>

							<tbody
								v-for="contract in filteredContracts"
								:key="'contract-' + contract.id"
							>
								<!-- Contract -->
								<tr
									class="main-row"
									style="cursor: pointer"
									@click="toggleContract(contract.id)"
								>
									<td style="width: 20px; text-align: center">
										<div class="expand-icon-wrapper">
											<vs-icon
												:icon="
													expandedContracts[contract.id]
														? 'keyboard_arrow_down'
														: 'keyboard_arrow_right'
												"
												size="small"
												style="pointer-events: none"
											/>
										</div>
									</td>
									<td>
										<span
											class="hvr-underline"
											@click.stop="
												displaySocietyPopup(contract.contract_society_id)
											"
										>
											<strong>{{ contract.society_name }}</strong>
										</span>
									</td>
									<td>
										<div @click.stop="() => {}" class="consultant-cell">
											<avatar-message-chip
												:user="{
													users_id: contract.consultant_id,
													name: contract.consultant_name,
												}"
												:contract="contract"
											/>
										</div>
									</td>
									<td>
										<span
											class="hvr-underline hvr-underline-out"
											@click.stop="displayContractPopup(contract.id)"
										>
											{{ contract.id }}
										</span>
									</td>
									<td>
										<!--
											Today's status. A contract that has finished since was
											still running in an older period, so flag the ones whose
											end date falls after the period being viewed.
										-->
										<div class="status-cell">
											<vs-chip
												:color="getContractStatusColor(contract.contract_status)"
											>
												{{ getContractStatus(contract.contract_status) }}
											</vs-chip>
											<vx-tooltip
												v-if="
													contract.was_running_in_period &&
													contract.contract_status !== 1
												"
												:text="stillRunningLabel(contract)"
												position="top"
											>
												<vs-icon
													icon="history"
													size="small"
													color="primary"
												></vs-icon>
											</vx-tooltip>
										</div>
									</td>
									<td class="text-center">{{ selectedPeriod }}</td>
									<td class="text-center">
										{{ formatCurrency(contract.display_budget) }}
									</td>
									<td class="text-center">
										{{ formatCurrency(contract.last_budget_rest) }}
									</td>
									<td class="text-center">
										{{ formatCurrency(contract.corrected_budget) }}
									</td>
									<td class="text-center">
										<b>{{ formatCurrency(contract.total_budget) }}</b>
									</td>
									<td class="text-center">
										<b>{{ formatCurrency(contract.total_sold_price) }}</b>
									</td>
									<td class="text-center">
										<span
											:class="
												contract.budget_rest >= 0 ? 'positive' : 'negative'
											"
										>
											{{ formatCurrency(contract.budget_rest) }}
										</span>
									</td>
									<td class="text-center">
										<div style="display: flex; justify-content: center">
											<vx-tooltip text="Backlinks" position="top">
												<vs-button
													size="small"
													color="primary"
													type="filled"
													icon-pack="feather"
													icon="icon-link"
													@click.stop="
														displayBacklinksPopup(contract, contract.items[0])
													"
												></vs-button>
											</vx-tooltip>
										</div>
									</td>
								</tr>

								<!-- Prestations -->
								<template v-if="expandedContracts[contract.id]">
									<tr
										class="expanded-item-row"
										v-for="mission in contract.items"
										:key="'mission-' + mission.mission_id"
									>
										<td></td>
										<td colspan="2">
											<div class="nested-label">
												<span>{{ mission.mission_name }}</span>
												<vs-chip
													v-if="mission.is_pause"
													color="warning"
													class="pause-chip"
													>En pause</vs-chip
												>
											</div>
										</td>
										<td>{{ mission.mission_id }}</td>
										<td>
											<!-- Prestations share the contract status codes -->
											<vs-chip
												:color="getContractStatusColor(mission.mission_status)"
											>
												{{ getContractStatus(mission.mission_status) }}
											</vs-chip>
										</td>
										<td class="text-center">
											{{ mission.recurrence == 0 ? "One shot" : "Mensuel" }}
										</td>
										<td class="text-center">
											<!--
												For a one-shot prestation this is the headline amount,
												shown for reference in every period. It is only funded
												in its start month, so outside that month it is greyed
												to make clear it is not counted in Total Budget.
											-->
											<span
												:class="{
													'reference-amount':
														mission.display_budget_is_reference,
												}"
												:title="
													mission.display_budget_is_reference
														? 'Montant one shot, pour référence — non ajouté au Total Budget de cette période'
														: ''
												"
											>
												{{ formatCurrency(mission.display_budget) }}
											</span>
										</td>
										<td class="text-center">
											{{ formatCurrency(mission.last_budget_rest) }}
										</td>
										<td class="text-center">
											<!-- Saving -->
											<div
												v-if="savingMissions[mission.mission_id]"
												class="correction-cell"
											>
												<CircularLoader :size="20" />
											</div>
											<!-- Editing -->
											<div
												v-else-if="editingMissions[mission.mission_id]"
												class="correction-cell"
												@click.stop
											>
												<vs-input
													v-model="
														editingMissions[mission.mission_id].corrected_budget
													"
													type="number"
													step="0.01"
													class="edit-input"
													placeholder="Montant"
												/>
												<vs-button
													size="small"
													color="success"
													type="flat"
													icon-pack="feather"
													icon="icon-check"
													@click.stop="
														saveCorrectedBudgetMission(mission, contract)
													"
												></vs-button>
												<vs-button
													size="small"
													color="danger"
													type="flat"
													icon-pack="feather"
													icon="icon-x"
													@click.stop="cancelEditMission(mission.mission_id)"
												></vs-button>
											</div>
											<!-- Display -->
											<div v-else class="correction-cell">
												<span>{{
													formatCurrency(mission.corrected_budget)
												}}</span>
												<!--
													vx-tooltip rather than a CSS tooltip: it relocates
													itself to <body>, so it is not clipped by the
													overflow:hidden on the table cells.
												-->
												<vx-tooltip
													v-if="mission.correct_budget_note"
													:text="mission.correct_budget_note"
													position="top"
												>
													<vs-icon
														icon="info_outline"
														size="small"
														color="#9a9a9a"
													></vs-icon>
												</vx-tooltip>
												<vs-button
													size="small"
													color="primary"
													type="flat"
													icon-pack="feather"
													icon="icon-edit"
													@click.stop="startEditMission(mission)"
												></vs-button>
											</div>
										</td>
										<td class="text-center">
											{{ formatCurrency(mission.total_budget) }}
										</td>
										<td class="text-center">
											{{ formatCurrency(mission.total_sold_price) }}
											<span class="netlink-count"
												>({{ mission.netlinks_count }})</span
											>
										</td>
										<td class="text-center">
											<span
												:class="
													mission.budget_rest >= 0 ? 'positive' : 'negative'
												"
											>
												{{ formatCurrency(mission.budget_rest) }}
											</span>
										</td>
										<td class="text-center">
											<div style="display: flex; justify-content: center">
												<vx-tooltip text="Backlinks" position="top">
													<vs-button
														size="small"
														color="primary"
														type="filled"
														icon-pack="feather"
														icon="icon-link"
														@click.stop="
															displayBacklinksPopup(contract, mission)
														"
													></vs-button>
												</vx-tooltip>
											</div>
										</td>
									</tr>
								</template>
							</tbody>
						</table>

						<!--
							Distinguish "the period is genuinely empty" from "your filters
							hid everything" - they look identical otherwise.
						-->
						<div v-if="filteredContracts.length === 0" class="empty-state">
							<template v-if="beforeEpoch">
								Le suivi budgétaire commence en {{ epochPeriod }}. Aucun report
								n'existe avant cette période.
							</template>
							<template v-else-if="allContracts.length === 0">
								Aucun budget ni backlink pour {{ selectedPeriod }}.
							</template>
							<template v-else>
								{{ allContracts.length }} contrat(s) en
								{{ selectedPeriod }}, tous masqués par les filtres en cours.
								<vs-button
									size="small"
									type="flat"
									color="primary"
									@click="resetAllFilter()"
								>
									Réinitialiser les filtres
								</vs-button>
							</template>
						</div>
					</div>

					<div v-else class="loading-container">
						<CircularLoader />
					</div>
				</div>
			</div>
		</vs-popup>

		<!-- Outside the fullscreen popup so it is not trapped behind it -->
		<CorrectionNotePopup
			:popupActive.sync="showNotePopup"
			:missionName="pendingCorrection ? pendingCorrection.item.mission_name : ''"
			:period="selectedPeriod"
			:amountLabel="
				pendingCorrection ? formatCurrency(pendingCorrection.amount) : ''
			"
			:existingNote="correctionNote"
			:isSaving="isSavingNote"
			@save="confirmCorrectionWithNote"
			@skip="skipCorrectionNote"
		/>

		<!-- Also outside the fullscreen popup, for the same z-index reason -->
		<NetlinksBudgetExportPopup :popupActive.sync="showExportPopup" />
	</div>
</template>

<script>
import Vue from "vue";
import VueCookies from "vue-cookies";
import moment from "moment";
import dbFieldConvertor from "@/mixins/dbFieldConvertorMixin";
import FilterCard from "@/views/filters/FilterCard.vue";
import store from "@/store/store";
import CircularLoader from "../widgets/loader/CircularLoader.vue";
import Netlink from "../../entities/Netlink";
import AvatarMessageChip from "../chips/AvatarMessageChip.vue";
import BacklinksContractPopup from "./BacklinksContractPopup.vue";
import ContractInfoPopup from "./ContractInfoPopup.vue";
import SocietyInfoPopup from "./SocietyInfoPopup.vue";
import CorrectionNotePopup from "./CorrectionNotePopup.vue";
import NetlinksBudgetExportPopup from "./NetlinksBudgetExportPopup.vue";

/**
 * Budget recap computed live, as contract -> prestation.
 *
 * Same columns as BacklinksManagementPopup ("Afficher les contrats et
 * budgets"), but the figures come from POST /netlink/getLiveBudgetRecap, which
 * replays each prestation month by month instead of reading
 * netlinks_budget_sheet. Nothing shown here can be a stale stored row.
 */
export default {
	name: "NetlinksBoughtPopup",
	mixins: [dbFieldConvertor],
	props: {
		popupActive: Boolean,
	},
	components: {
		CircularLoader,
		FilterCard,
		AvatarMessageChip,
		CorrectionNotePopup,
		NetlinksBudgetExportPopup,
		// BacklinksContractPopup is mounted imperatively via Vue.extend, so it is
		// deliberately not registered here.
	},

	data() {
		return {
			allContracts: [],
			totals: {},
			coverage: {
				unassigned_count: 0,
				unassigned_amount: 0,
				refused_count: 0,
			},
			accruingAfterEnd: [],
			// Budget tracking starts at this period; nothing exists before it.
			epochPeriod: null,
			beforeEpoch: false,
			isLoading: false,

			expandedContracts: {},
			allExpanded: false,

			// Correction antérieur editing
			editingMissions: {},
			savingMissions: {},
			showNotePopup: false,
			correctionNote: "",
			pendingCorrection: null,
			isSavingNote: false,
			showExportPopup: false,

			selectedPeriod: moment().format("MM/YYYY"),
			filterPeriod: moment().format("MM/YYYY"),

			filterRefreshKey: 0,
			filtersTemplates: [
				{
					order: 0,
					emitSlugName: "filter_society",
					filterPlaceHolder: "Société",
					value: "",
					type: "Input",
				},
				{
					order: 1,
					emitSlugName: "filter_contract_id",
					filterPlaceHolder: "ID Contrat",
					value: "",
					type: "Input",
				},
				{
					order: 2,
					emitSlugName: "filter_status",
					filterPlaceHolder: "Statut",
					value: 1,
					type: "Dropdown",
					params: {
						autocomplete: true,
						dropdownValues: [
							{ name: "Actif", id: 1 },
							{ name: "A Valider", id: 2 },
							{ name: "A venir", id: 8 },
							{ name: "En pause", id: 3 },
							{ name: "Terminé", id: 4 },
							// Annulé (5) is excluded server-side, so no option for it
							{ name: "Relance", id: 9 },
						],
					},
				},
				{
					order: 3,
					emitSlugName: "filter_consultant",
					filterPlaceHolder: "Consultant",
					value: "",
					type: "Dropdown",
					params: {
						autocomplete: true,
						dropdownValues: [],
					},
				},
				{
					order: 4,
					emitSlugName: "filter_period",
					filterPlaceHolder: "Période",
					value: "",
					require: true,
					type: "Date",
					params: {
						date_type: "period",
					},
				},
			],
		};
	},

	created() {
		this.fetchLiveBudget();
		this.fetchConsultants();
		store.dispatch("getAllContractsWithContractsUsers");
		this.getConsultantDefault();
	},

	computed: {
		allCollaborators: () => store.state.collaborators,
		societies: () => store.state.societies,
		allContractsWithContractsUsers: () => store.state.contracts_contracts_users,
		activeUser: () => VueCookies.get("userInfo"),

		filteredContracts() {
			return this.allContracts.filter((contract) => {
				const societyFilter = this.filtersTemplates[0].value;
				const societyMatch =
					!societyFilter ||
					(contract.society_name || "")
						.toLowerCase()
						.includes(String(societyFilter).toLowerCase());

				const contractIdFilter = this.filtersTemplates[1].value;
				const contractIdMatch =
					!contractIdFilter ||
					String(contract.id).includes(String(contractIdFilter));

				/*
				 * Statut filters on today's status and is deliberately NOT preselected:
				 * contracts.status is a present-tense value, so filtering an old period
				 * by "Actif" would hide every contract that has finished since - 19 to
				 * 21 of them per period, all of which were genuinely running then.
				 *
				 * Compared as strings: the dropdown emits the id as a number on first
				 * render and as a string once the user picks a value.
				 */
				const statusFilter = this.filtersTemplates[2].value;
				const statusMatch =
					!statusFilter ||
					String(statusFilter) === String(contract.contract_status);

				// Kept as soon as one prestation belongs to the selected consultant.
				const consultantFilter = this.filtersTemplates[3].value;
				const consultantMatch =
					!consultantFilter ||
					(contract.consultant_ids || []).some(
						(id) => String(id) === String(consultantFilter)
					);

				return (
					societyMatch && contractIdMatch && statusMatch && consultantMatch
				);
			});
		},

		totalBudgets() {
			return this.sumVisible("total_budget");
		},

		totalSpent() {
			return this.sumVisible("total_sold_price");
		},

		totalAvailable() {
			return this.sumVisible("budget_rest");
		},

		totalNetlinks() {
			return this.sumVisible("netlinks_count");
		},

		accruingAfterEndLabel() {
			const total = this.accruingAfterEnd.reduce(
				(sum, issue) => sum + (parseFloat(issue.monthly_budget) || 0),
				0
			);
			return `${this.formatCurrency(total)} / mois`;
		},
	},

	watch: {
		filterPeriod(newValue) {
			this.selectedPeriod = newValue;
			// The period is a server-side filter: the whole chain is recomputed.
			this.fetchLiveBudget();
		},
	},

	methods: {
		/** Pre-select the logged-in user in the Consultant filter. */
		getConsultantDefault() {
			const template = this.filtersTemplates.find(
				(row) => row.emitSlugName === "filter_consultant"
			);
			if (template && this.activeUser) template.value = this.activeUser.id;
		},

		/** vx-tooltip takes plain text, so the sentence is built here. */
		stillRunningLabel(contract) {
			const end = contract.contract_end_month ?
				` (fin : ${contract.contract_end_month})` :
				"";
			return (
				`Statut actuel : ${this.getContractStatus(contract.contract_status)}, ` +
				`mais le contrat était encore en cours en ${this.selectedPeriod}${end}.`
			);
		},

		startEditMission(mission) {
			this.$set(this.editingMissions, mission.mission_id, {
				corrected_budget: mission.corrected_budget || 0,
			});
		},

		cancelEditMission(missionId) {
			this.$delete(this.editingMissions, missionId);
		},

		/**
		 * The correction is saved immediately. The note popup opens afterwards and
		 * is optional - "Passer" closes it and the correction stays saved.
		 */
		async saveCorrectedBudgetMission(mission, contract) {
			const missionId = mission.mission_id;
			const amount =
				parseFloat((this.editingMissions[missionId] || {}).corrected_budget) ||
				0;

			const saved = await this.performCorrectionSave(mission, contract);
			if (!saved) return;

			this.pendingCorrection = { item: mission, contract: contract, amount: amount };
			this.correctionNote = mission.correct_budget_note || "";
			this.showNotePopup = true;
		},

		async performCorrectionSave(mission, contract) {
			const missionId = mission.mission_id;
			const edited = this.editingMissions[missionId];
			if (!missionId || !edited) return false;

			try {
				this.$set(this.savingMissions, missionId, true);
				await Netlink.updateLiveCorrection({
					mission_id: missionId,
					contract_id: contract.id,
					period: this.selectedPeriod,
					corrected_budget: parseFloat(edited.corrected_budget) || 0,
					// correct_budget_note omitted on purpose: the note is saved
					// separately, and sending nothing leaves any existing one alone.
				});
				this.$delete(this.editingMissions, missionId);
				await this.fetchLiveBudget();
				this.$vs.notify({
					title: "Succès",
					text: "Correction enregistrée",
					color: "success",
				});
				return true;
			} catch (error) {
				console.error("Error saving correction:", error);
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de l'enregistrement de la correction",
					color: "danger",
				});
				return false;
			} finally {
				this.$delete(this.savingMissions, missionId);
			}
		},

		/** Close without a note. The correction itself is already saved. */
		skipCorrectionNote() {
			this.showNotePopup = false;
			this.pendingCorrection = null;
			this.correctionNote = "";
		},

		async confirmCorrectionWithNote(note) {
			if (!this.pendingCorrection) return;
			const { item, contract } = this.pendingCorrection;
			this.isSavingNote = true;
			try {
				await Netlink.updateLiveCorrection({
					mission_id: item.mission_id,
					contract_id: contract.id,
					period: this.selectedPeriod,
					corrected_budget: this.pendingCorrection.amount,
					correct_budget_note: note || "",
				});
				await this.fetchLiveBudget();
				this.$vs.notify({
					title: "Succès",
					text: "Note enregistrée",
					color: "success",
				});
			} catch (error) {
				console.error("Error saving correction note:", error);
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de l'enregistrement de la note",
					color: "danger",
				});
			} finally {
				this.isSavingNote = false;
				this.showNotePopup = false;
				this.pendingCorrection = null;
				this.correctionNote = "";
			}
		},

		sumVisible(key) {
			return this.filteredContracts.reduce(
				(sum, contract) => sum + (contract[key] || 0),
				0
			);
		},

		async fetchLiveBudget() {
			try {
				this.isLoading = true;
				const response = await Netlink.getLiveBudgetRecap({
					period: this.selectedPeriod,
				});

				const toNumber = (row, keys) => {
					keys.forEach((key) => {
						row[key] = parseFloat(row[key]) || 0;
					});
					return row;
				};
				const moneyKeys = [
					"mission_budget",
					"display_budget",
					"last_budget_rest",
					"corrected_budget",
					"total_budget",
					"total_sold_price",
					"budget_rest",
				];

				this.allContracts = (response.contracts || []).map((contract) => ({
					...toNumber(contract, moneyKeys),
					items: (contract.items || []).map((mission) =>
						toNumber({ ...mission }, moneyKeys)
					),
				}));
				this.totals = response.totals || {};
				this.coverage = response.coverage || {
					unassigned_count: 0,
					unassigned_amount: 0,
					refused_count: 0,
				};
				this.accruingAfterEnd =
					(response.data_issues && response.data_issues.accruing_after_end) ||
					[];
				this.epochPeriod = response.epoch_period || null;
				this.beforeEpoch = Boolean(response.before_epoch);

				this.expandedContracts = {};
				this.allExpanded = false;
			} catch (error) {
				console.error("Error fetching live budget recap:", error);
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors du chargement des budgets",
					color: "danger",
				});
			} finally {
				this.isLoading = false;
			}
		},

		async fetchConsultants() {
			try {
				await store.dispatch("getAllCollaborators");
				const consultants = this.allCollaborators
					.filter((collaborator) => [3, 6, 7, 9].includes(collaborator.type))
					.sort((a, b) => a.firstname.localeCompare(b.firstname))
					.map((collaborator) => ({
						id: collaborator.id,
						name: `${collaborator.firstname} ${collaborator.lastname}`,
					}));

				this.filtersTemplates.find(
					(f) => f.emitSlugName === "filter_consultant"
				).params.dropdownValues = consultants;
				this.filterRefreshKey++;
			} catch (error) {
				console.error("Error fetching consultants:", error);
			}
		},

		toggleContract(contractId) {
			this.$set(
				this.expandedContracts,
				contractId,
				!this.expandedContracts[contractId]
			);
		},

		toggleAllExpand() {
			this.allExpanded = !this.allExpanded;
			const contracts = {};
			this.filteredContracts.forEach((contract) => {
				contracts[contract.id] = this.allExpanded;
			});
			this.expandedContracts = contracts;
		},

		formatCurrency(amount) {
			if (amount === null || amount === undefined) return "0,00 €";
			return new Intl.NumberFormat("fr-FR", {
				style: "currency",
				currency: "EUR",
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(amount);
		},

		handleFilterEmits([emitSlugName, value]) {
			const filter = this.filtersTemplates.find(
				(f) => f.emitSlugName === emitSlugName
			);
			if (!filter) return;

			if (value === null || value === undefined) {
				filter.value = "";
			} else if (typeof value === "object") {
				filter.value = value.value || value.id || "";
			} else {
				filter.value = value;
			}

			if (emitSlugName === "filter_contract_id" && filter.value !== "") {
				filter.value = String(filter.value);
			}
			if (emitSlugName === "filter_consultant" && filter.value !== "") {
				filter.value = String(filter.value);
			}
			if (emitSlugName === "filter_period" && filter.value !== "") {
				this.filterPeriod = filter.value;
			}
		},

		resetAllFilter() {
			this.filtersTemplates.forEach((filter) => {
				filter.value = "";
			});
			this.filterPeriod = moment().format("MM/YYYY");
			this.filterRefreshKey++;
		},

		/** Same popup the budget table opens from its Actions column. */
		displayBacklinksPopup(contract, mission) {
			const ComponentClass = Vue.extend(BacklinksContractPopup);
			const backlinksContractPopup = new ComponentClass({
				propsData: {
					popupActive: true,
					contractId: contract.id,
					societyName: contract.society_name,
					societyId: contract.contract_society_id,
					mission: mission || {},
				},
			});
			backlinksContractPopup.$mount();
			backlinksContractPopup.$on("data-updated", () => {
				this.fetchLiveBudget();
			});
			this.$refs.popupContainer.appendChild(backlinksContractPopup.$el);
		},

		getSocietyById(societyId) {
			return this.societies.find((society) => society.id == societyId);
		},

		/* Both popups expect a whole object, not an id - same wiring as
		 * BacklinksManagementPopup. */
		displaySocietyPopup(societyId) {
			const society = this.getSocietyById(societyId);
			if (!society) return;

			const selectedSociety = JSON.parse(JSON.stringify(society));
			this.$http
				.get(this.apiUrl + "society/getImg/" + societyId)
				.then((image) => {
					selectedSociety.image = image.data.length > 0 ? image.data : null;
				})
				.catch(() => {});

			const ComponentClass = Vue.extend(SocietyInfoPopup);
			const societyPopup = new ComponentClass({
				propsData: {
					society: selectedSociety,
					popupActive: true,
					router: this.$router,
					apiUrl: this.apiUrl,
				},
			});
			societyPopup.$mount();
			this.$refs.popupContainer.appendChild(societyPopup.$el);
		},

		displayContractPopup(contractId) {
			const found = (this.allContractsWithContractsUsers || []).find(
				(row) => row.id == contractId
			);
			if (!found) {
				this.$vs.notify({
					title: "Erreur",
					text: "Contrat introuvable, contactez l'équipe technique.",
				});
				return;
			}

			const ComponentClass = Vue.extend(ContractInfoPopup);
			const contractPopup = new ComponentClass({
				propsData: {
					contract: found,
					popupActive: true,
					router: this.$router,
					highZIndex: true,
				},
			});
			contractPopup.$mount();
			this.$refs.popupContainer.appendChild(contractPopup.$el);
		},
	},
};
</script>

<style scoped>
.vs-popup {
	height: 100vh !important;
}

.vs-popup__content {
	height: 100% !important;
	display: flex;
	flex-direction: column;
}

.popup-container {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
}

.filter-section {
	flex-shrink: 0;
}

.export-bar {
	flex-shrink: 0;
	display: flex;
	justify-content: flex-end;
	margin-top: 0.5rem;
}

.budget-summary {
	flex-shrink: 0;
	padding: 0;
	margin-top: 0.5rem !important;
}

.table-container {
	flex: 2;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

.custom-table-wrapper {
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: auto;
}

.custom-budget-table {
	width: 100%;
	border-collapse: collapse;
	background: white;
}

.custom-budget-table thead {
	background-color: #0c293d !important;
	position: sticky;
	top: 0;
	z-index: 10;
}

.custom-budget-table thead th {
	background-color: #0c293d !important;
	color: white !important;
	padding: 1px 5px !important;
	text-align: left;
	font-weight: 500;
	border-bottom: 2px solid #0a1f2e;
	height: 18px !important;
	line-height: 1.1;
	font-size: 11px;
}

.custom-budget-table tbody tr {
	height: 18px !important;
	border-bottom: 1px solid #f0f0f0ff;
}

.custom-budget-table tbody tr.main-row:hover {
	background-color: #e8f4f9;
}

.custom-budget-table td {
	padding: 1px 5px !important;
	vertical-align: middle;
	line-height: 1.1;
	font-size: 11px;
	height: 18px !important;
	overflow: hidden;
}

.custom-budget-table .expanded-item-row {
	background-color: #fafbfc !important;
	height: 20px !important;
}

.custom-budget-table .expanded-item-row:hover {
	background-color: #e8f4f9 !important;
}

.custom-budget-table .expanded-item-row td {
	padding: 3px 5px !important;
	font-size: 11px;
	height: 20px !important;
}

.nested-label {
	display: flex;
	align-items: center;
	gap: 4px;
	padding-left: 12px;
	font-weight: 600;
}

.pause-chip {
	transform: scale(0.85);
}

.netlink-count {
	color: #888;
	font-size: 10px;
}

.correction-cell {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 3px;
}

.correction-cell .edit-input {
	width: 90px;
	margin: 0;
}

.custom-budget-table .vs-button {
	height: 16px !important;
	min-height: 16px !important;
	padding: 13px 3px !important;
	font-size: 9px !important;
}

.custom-budget-table .vs-button .vs-icon {
	font-size: 11px !important;
}

.custom-budget-table .vs-chip {
	height: 16px !important;
	padding: 0px 5px !important;
	font-size: 9px !important;
	line-height: 1.1 !important;
}

.custom-budget-table .expand-icon-wrapper {
	padding: 1px !important;
	display: flex;
	justify-content: center;
	align-items: center;
}

.custom-budget-table .expand-icon-wrapper .vs-icon {
	font-size: 14px !important;
	width: 14px !important;
	height: 14px !important;
	line-height: 14px !important;
}

.consultant-cell {
	display: flex;
	align-items: center;
	justify-content: center;
}

.status-cell {
	display: flex;
	align-items: center;
	gap: 2px;
}

/* One-shot amount repeated for reference; not part of this period's total */
.reference-amount {
	color: #9a9a9a;
	font-style: italic;
}

/* Tooltips are vx-tooltip, which relocates itself to <body> - a CSS tooltip
   would be clipped by the overflow:hidden on the table cells. */
.custom-budget-table .con-vs-tooltip {
	display: inline-flex;
	align-items: center;
	cursor: pointer;
}

.coverage-warning {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	gap: 0.4rem;
	margin: 0.25rem 0;
	padding: 0.35rem 0.6rem;
	border-radius: 4px;
	background-color: #fff8e1;
	color: #8a6d3b;
	font-size: 11px;
}

.empty-state {
	padding: 2rem;
	text-align: center;
	color: #888;
	font-size: 0.9rem;
}

.loading-container {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 400px;
}

.summary-cards {
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
}

.summary-card {
	flex: 1;
	min-width: 150px;
	padding: 0.5rem 0.75rem !important;
}

.summary-card .vs-card__content {
	padding: 0 !important;
}

.summary-content {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}

.summary-content h4 {
	margin: 0 !important;
	color: #666;
	font-size: 0.825rem;
	text-transform: uppercase;
	letter-spacing: 0.3px;
	line-height: 1.2;
}

.summary-content p {
	margin: 0 !important;
	font-size: 0.75rem;
}

.summary-content .flex {
	align-items: center;
	gap: 0.5rem;
}

.summary-amount {
	font-size: 1.25rem;
	font-weight: bold;
	font-family: "Monaco", "Menlo", monospace;
	line-height: 1.2;
}

.summary-amount.spent {
	color: #d32f2f;
}

.positive {
	color: #388e3c;
	font-weight: bold;
}

.negative {
	color: #d32f2f;
	font-weight: bold;
}
</style>
